import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import ApiInspector from '../../../components/ApiInspector';
import { showToast } from '../../../components/Toast';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './InsegnePage.api';

const MODULO = 'anagrafiche.insegne';

// SVG Icons
const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path>
  </svg>
);
const TagIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);
const RiattivaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10"></polyline><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
  </svg>
);
const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

interface InsegnaCliente {
  id: number;
  societaId: number;
  societa?: { id: number; nome: string };
  codice: string;
  nome: string;
  note: string | null;
  attivo: boolean;
  _count?: { codiciArticolo: number; clienti: number };
}

interface NuovaInsegna {
  societaId: string;
  codice: string;
  nome: string;
  note: string;
}

const NUOVA_VUOTA: NuovaInsegna = { societaId: '', codice: '', nome: '', note: '' };

export default function InsegnePage() {
  const navigate = useNavigate();
  const [insegne, setInsegne] = useState<InsegnaCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [mostraDisattivate, setMostraDisattivate] = useState(false);

  // Modale creazione (la modifica si fa dalla scheda)
  const [showModal, setShowModal] = useState(false);
  const [nuova, setNuova] = useState<NuovaInsegna>(NUOVA_VUOTA);
  // Il codice segue il nome finché l'utente non lo scrive a mano
  const [codiceManuale, setCodiceManuale] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // Permessi: sulla società della riga, perché il SUPERADMIN in "Tutte le
  // società" vede insegne di più società nella stessa lista
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const isDirty = showModal && (!!nuova.nome.trim() || !!nuova.codice.trim() || !!nuova.note.trim());
  useDirtyForm(isDirty, 'Nuova insegna');

  const loadInsegne = useCallback(async () => {
    if (!pronta) return;
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (filterSocietaId) p.append('societaId', filterSocietaId);
      if (mostraDisattivate) p.append('includiDisattivate', '1');
      // @api GET /api/anagrafiche/insegne?societaId=N&includiDisattivate=1 -> [{"id":3,"societaId":1,"societa":{"id":1,"nome":"Smartix"},"codice":"BRICO","nome":"Bricocenter","note":null,"attivo":true,"_count":{"codiciArticolo":40,"clienti":2}}]
      const data = await apiFetch<InsegnaCliente[]>(`/api/anagrafiche/insegne?${p}`);
      setInsegne(data);
    } catch (err: any) {
      console.error('[InsegnePage][loadInsegne]', err);
      showToast(err.message || 'Errore nel caricamento delle insegne', 'error');
    } finally {
      setLoading(false);
    }
  }, [pronta, filterSocietaId, mostraDisattivate]);

  useEffect(() => {
    loadInsegne();
  }, [loadInsegne]);

  const filteredInsegne = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return insegne;
    return insegne.filter((i) => i.nome.toLowerCase().includes(term) || i.codice.toLowerCase().includes(term));
  }, [searchTerm, insegne]);

  const apriScheda = (insegna: InsegnaCliente) => navigate(`/anagrafiche/insegne/${insegna.id}`);

  const handleCreate = () => {
    setNuova({ ...NUOVA_VUOTA, societaId: filterSocietaId });
    setCodiceManuale(false);
    setError('');
    setShowModal(true);
  };

  const chiudiModale = () => {
    if (saving) return;
    setShowModal(false);
  };

  const disattiva = async (insegna: InsegnaCliente) => {
    if (!window.confirm(
      `Disattivare l'insegna "${insegna.nome}"?\n\n` +
      'Non comparirà più nelle tendine di nominativi, contratti, premi e import ordini. ' +
      'Clienti, codici e ordini già collegati la mantengono. Si può riattivare in qualsiasi momento.'
    )) return;
    try {
      // @api DELETE /api/anagrafiche/insegne/:id -> {"message":"Insegna disattivata"}
      await apiFetch(`/api/anagrafiche/insegne/${insegna.id}`, { method: 'DELETE' });
      showToast('Insegna disattivata', 'success');
      loadInsegne();
    } catch (err: any) {
      showToast(err.message || "Errore nella disattivazione dell'insegna", 'error');
    }
  };

  const riattiva = async (insegna: InsegnaCliente) => {
    try {
      // @api POST /api/anagrafiche/insegne/:id/riattiva -> {"success":true}
      await apiFetch(`/api/anagrafiche/insegne/${insegna.id}/riattiva`, { method: 'POST' });
      showToast('Insegna riattivata', 'success');
      loadInsegne();
    } catch (err: any) {
      showToast(err.message || "Errore nella riattivazione dell'insegna", 'error');
    }
  };

  const handleSave = async () => {
    if (!nuova.codice.trim() || !nuova.nome.trim()) {
      setError('Codice e nome sono obbligatori');
      return;
    }
    if (!nuova.societaId) {
      setError('Seleziona la società');
      return;
    }

    try {
      setSaving(true);
      setError('');
      // @api POST /api/anagrafiche/insegne {"societaId":1,"codice":"BRICO","nome":"Bricocenter","note":null} -> {"id":3,"societaId":1,"codice":"BRICO","nome":"Bricocenter","note":null,"attivo":true}
      const creata = await apiFetch<InsegnaCliente>('/api/anagrafiche/insegne', {
        method: 'POST',
        body: JSON.stringify({
          codice: nuova.codice.trim(),
          nome: nuova.nome.trim(),
          note: nuova.note.trim() || null,
          societaId: parseInt(nuova.societaId, 10),
        }),
      });
      setShowModal(false);
      showToast('Insegna creata', 'success');
      navigate(`/anagrafiche/insegne/${creata.id}`);
    } catch (err: any) {
      setError(err.message || "Errore nel salvataggio dell'insegna");
    } finally {
      setSaving(false);
    }
  };

  const handleNomeChange = (nome: string) => {
    setNuova((prev) => ({
      ...prev,
      nome,
      codice: codiceManuale ? prev.codice : nome.substring(0, 6).toUpperCase().replace(/[^A-Z0-9]/g, ''),
    }));
  };

  const handleCodiceChange = (codice: string) => {
    // Codice svuotato: torna a seguire il nome
    setCodiceManuale(codice.trim() !== '');
    setNuova((prev) => ({ ...prev, codice: codice.toUpperCase() }));
  };

  const columns = useMemo<BopExcelColumn<InsegnaCliente>[]>(() => {
    const cols: BopExcelColumn<InsegnaCliente>[] = [
      {
        key: 'codice',
        header: 'Codice',
        accessor: (i) => i.codice,
        filter: { type: 'text' },
        width: '160px',
        cellClassName: 'bop-insegne__td-codice',
      },
      {
        key: 'nome',
        header: 'Nome',
        accessor: (i) => i.nome,
        filter: { type: 'text' },
        cellClassName: 'bop-insegne__td-nome',
      },
    ];
    if (!filterSocietaId) {
      cols.push({
        key: 'societa',
        header: 'Società',
        accessor: (i) => i.societa?.nome ?? '',
        filter: { type: 'text' },
        width: '200px',
      });
    }
    cols.push(
      {
        key: 'clienti',
        header: 'Clienti',
        accessor: (i) => i._count?.clienti ?? 0,
        width: '110px',
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
      },
      {
        key: 'codiciArticolo',
        header: 'Codici articolo',
        accessor: (i) => i._count?.codiciArticolo ?? 0,
        width: '150px',
        cellClassName: 'bop-text-right',
        headerClassName: 'bop-text-right',
      },
    );
    if (mostraDisattivate) {
      cols.push({
        key: 'stato',
        header: 'Stato',
        accessor: (i) => (i.attivo ? 'Attiva' : 'Disattivata'),
        filter: { type: 'text' },
        width: '130px',
        render: (i) => (
          <span className={`bop-badge ${i.attivo ? 'bop-badge--success' : 'bop-badge--neutral'}`}>
            {i.attivo ? 'Attiva' : 'Disattivata'}
          </span>
        ),
      });
    }
    return cols;
  }, [filterSocietaId, mostraDisattivate]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Insegne Clienti</h2>
          <p>Le catene a cui appartengono i clienti: clienti collegati, codici articolo, contratti e premi</p>
        </div>
        {canEdit && (
          <button className="bop-btn bop-btn-primary" onClick={handleCreate}>
            <PlusIcon />
            Nuova Insegna
          </button>
        )}
      </div>

      {/* Filtri */}
      <div className="bop-card bop-insegne__filter-card">
        <div className="bop-insegne__filter-row">
          {!isUnica && (
            <div className="bop-insegne__filter-societa">
              <select
                value={filterSocietaId}
                onChange={(e) => setFilterSocietaId(e.target.value)}
                className="bop-input bop-insegne__select-societa"
              >
                <option value="">Tutte le società</option>
                {societaList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="bop-insegne__search-wrap">
            <div className="bop-insegne__search-icon">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per nome o codice..."
              className="bop-input bop-insegne__search-input"
            />
          </div>
          <label className="bop-insegne__check">
            <input
              type="checkbox"
              checked={mostraDisattivate}
              onChange={(e) => setMostraDisattivate(e.target.checked)}
            />
            Mostra disattivate
          </label>
        </div>
      </div>

      {/* Tabella Excel-like: il clic sulla riga apre la scheda */}
      <BopExcelTable<InsegnaCliente>
        data={filteredInsegne}
        loading={loading}
        rowKey={(i) => i.id}
        onRowClick={apriScheda}
        initialSort={{ key: 'nome', dir: 'asc' }}
        emptyState={
          <div className="bop-empty-state">
            <div className="bop-empty-state__icon">
              <TagIcon size={48} />
            </div>
            <h3 className="bop-empty-state__title">
              {searchTerm ? 'Nessuna insegna trovata' : 'Nessuna insegna'}
            </h3>
            <p className="bop-empty-state__text">
              {searchTerm
                ? 'Prova a modificare i criteri di ricerca'
                : 'Inizia creando la prima insegna cliente'}
            </p>
          </div>
        }
        columns={columns}
        rowActions={(insegna) => (
          <BopRowActions
            actions={[
              {
                icon: <RowIcon.Disable />, label: 'Disattiva', onClick: () => disattiva(insegna), variant: 'danger',
                hidden: !insegna.attivo || !can(MODULO, 'delete', insegna.societaId),
              },
              {
                icon: <RiattivaIcon />, label: 'Riattiva', onClick: () => riattiva(insegna), variant: 'primary',
                hidden: insegna.attivo || !can(MODULO, 'edit', insegna.societaId),
              },
            ]}
          />
        )}
      />

      {/* Modale Nuova insegna */}
      {showModal && (
        <div className="bop-modal-overlay" onClick={chiudiModale}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Nuova Insegna</h3>
              <button className="bop-btn-icon" onClick={chiudiModale} aria-label="Chiudi" disabled={saving}>
                <XIcon />
              </button>
            </div>

            <div className="bop-modal__body bop-insegne__modal-form">
              {error && (
                <div className="bop-insegne__error-banner">
                  {error}
                </div>
              )}

              {/* Società: solo se la pagina non ne ha già una (SUPERADMIN su "Tutte le società") */}
              {!filterSocietaId && (
                <div className="bop-form-group">
                  <label className="bop-label">
                    Società <span className="bop-text-danger">*</span>
                  </label>
                  <select
                    value={nuova.societaId}
                    onChange={(e) => setNuova({ ...nuova, societaId: e.target.value })}
                    className="bop-input"
                  >
                    <option value="">Seleziona società...</option>
                    {societaList.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">
                  Nome <span className="bop-text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={nuova.nome}
                  onChange={(e) => handleNomeChange(e.target.value)}
                  placeholder="Es. Conad, Coop, Esselunga..."
                  autoFocus
                  className="bop-input"
                />
              </div>

              <div className="bop-form-group">
                <label className="bop-label">
                  Codice <span className="bop-text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={nuova.codice}
                  onChange={(e) => handleCodiceChange(e.target.value)}
                  placeholder="Es. CONAD"
                  maxLength={20}
                  className="bop-input"
                />
                <span className="bop-insegne__hint">
                  Codice univoco dell'insegna nella società
                </span>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea
                  value={nuova.note}
                  onChange={(e) => setNuova({ ...nuova, note: e.target.value })}
                  rows={3}
                  placeholder="Note aggiuntive..."
                  className="bop-input bop-insegne__textarea"
                />
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={chiudiModale} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Crea e apri'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
